import React, { useState, useMemo } from 'react';
import { 
  Zap, MessageCircle, HelpCircle, GitFork, Tag, Clock, Sparkles, Calendar, Globe, 
  LayoutList, Lightbulb, ListFilter, Search, ChevronLeft, ChevronRight 
} from 'lucide-react';
import { Box, Paper, Group, Text, Badge, ActionIcon, TextInput, ScrollArea, ThemeIcon, Button, Stack } from '@mantine/core';
import { useTheme } from './ThemeContext';

export const SidebarPalette = ({ onAddNode }) => {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const nodeCategories = [
    {
      name: 'Messages & Menus',
      items: [
        {
          type: 'messageNode',
          title: 'WhatsApp Message',
          desc: 'Text, header media & buttons',
          icon: MessageCircle,
          color: 'teal',
          data: {
            label: 'Send Message',
            body: 'Hello! Thanks for contacting us.',
            headerType: 'NONE',
            buttons: [],
          },
        },
        {
          type: 'messageNode',
          title: 'Interactive List Menu',
          desc: 'Menu with up to 10 packages/classes',
          icon: ListFilter,
          color: 'cyan',
          data: {
            label: 'Choose Package / Menu',
            body: 'Please choose an option from the menu below to explore our gym memberships and plans:',
            headerType: 'NONE',
            interactiveType: 'list',
            listSource: 'packages',
            listCtaText: 'View Packages',
            listSectionTitle: 'Available Packages',
            listVariable: 'selected_package',
            branchScope: 'dynamic',
            listCustomItems: [],
            listBranching: false,
            buttons: [],
          },
        },
        {
          type: 'messageNode',
          title: 'Branch Selector Menu',
          desc: 'Menu to choose gym location / branch',
          icon: ListFilter,
          color: 'blue',
          data: {
            label: 'Choose Branch',
            body: 'Welcome! Please select your nearest gym branch to view local memberships and class schedules:',
            headerType: 'NONE',
            interactiveType: 'list',
            listSource: 'branches',
            listCtaText: 'Select Branch',
            listSectionTitle: 'Our Locations',
            listVariable: 'selected_branch_id',
            branchScope: 'all',
            listCustomItems: [],
            listBranching: false,
            buttons: [],
          },
        },
        {
          type: 'whatsappFlowNode',
          title: 'WhatsApp Form Flow',
          desc: 'Native in-app interactive form',
          icon: LayoutList,
          color: 'indigo',
          data: {
            label: 'WhatsApp Native Flow',
            flow_id: '',
            flow_name: '',
            cta_text: 'Open Form',
            header: '',
            body: 'Please complete this quick form by tapping below:',
            first_screen: 'LEAD_SCREEN',
          },
        },
      ],
    },
    {
      name: 'User Input & Booking',
      items: [
        {
          type: 'questionNode',
          title: 'Ask Question',
          desc: 'Capture user answer in variable',
          icon: HelpCircle,
          color: 'violet',
          data: {
            label: 'Collect Info',
            question: 'Which city or location are you looking for?',
            variable: '',
          },
        },
        {
          type: 'dateTimeNode',
          title: 'Date & Time Picker',
          desc: 'Appointment & slot booking',
          icon: Calendar,
          color: 'grape',
          data: {
            label: 'Select Date & Time',
            prompt: 'Please select your convenient date & time for the session:',
            dateOptions: ['Today', 'Tomorrow', 'Next Business Day'],
            slots: ['10:00 AM - 12:00 PM', '02:00 PM - 04:00 PM', '05:00 PM - 07:00 PM'],
            variable: '',
          },
        },
      ],
    },
    {
      name: 'Logic & Actions',
      items: [
        {
          type: 'conditionNode',
          title: 'Condition (If/Else)',
          desc: 'Branch flow based on variables',
          icon: GitFork,
          color: 'yellow',
          data: {
            label: 'Condition / Branch',
            variable: '',
            operator: 'equals',
            value: '',
          },
        },
        {
          type: 'actionNode',
          title: 'CRM Action / Tag',
          desc: 'Add or remove customer tag',
          icon: Tag,
          color: 'orange',
          data: {
            label: 'Add Tag',
            actionType: 'ADD_TAG',
            tag: '',
          },
        },
        {
          type: 'delayNode',
          title: 'Time Delay',
          desc: 'Wait for Drip sequence follow-up',
          icon: Clock,
          color: 'red',
          data: {
            label: 'Follow-up Delay',
            duration: '24',
            unit: 'hours',
          },
        },
      ],
    },
    {
      name: 'Integrations',
      items: [
        {
          type: 'apiNode',
          title: 'External Webhook / API',
          desc: 'Call External API or CRM',
          icon: Globe,
          color: 'blue',
          data: {
            label: 'External API / Webhook',
            method: 'GET',
            url: '',
            authType: 'none',
            authToken: '',
            headers: [],
            body: '',
            mappings: [],
          },
        },
      ],
    },
  ];

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return nodeCategories;
    const q = searchQuery.toLowerCase();
    return nodeCategories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) => item.title.toLowerCase().includes(q) || item.desc.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [searchQuery, nodeCategories]);

  return (
    <>
      {/* Floating Expand Pill on Canvas when Collapsed */}
      {isCollapsed && (
        <Button
          onClick={() => setIsCollapsed(false)}
          title="Open Flow Elements"
          variant="default"
          size="xs"
          radius="md"
          leftIcon={<Sparkles size={14} color="#dc2742" />}
          rightIcon={<ChevronRight size={14} />}
          sx={{
            position: 'absolute',
            left: 12,
            top: 12,
            zIndex: 30,
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
            borderColor: '#e2e8f0',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#dc2742',
              color: '#dc2742',
            },
          }}
        >
          Flow Elements
        </Button>
      )}

      {/* Main Sidebar Palette Panel */}
      <Paper
        id="tour-sidebar-palette"
        shadow="md"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 20,
          width: 288,
          minWidth: 288,
          maxWidth: 288,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDark ? '#1a1b1e' : '#ffffff',
          borderRight: isDark ? '1px solid #2C2E33' : '1px solid #E2E8F0',
          transform: isCollapsed ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          padding: '12px',
        }}
      >
        {/* Palette Header */}
        <Group position="apart" align="center" pb="xs" sx={{ borderBottom: isDark ? '1px solid #2C2E33' : '1px solid #F1F5F9' }}>
          <Group spacing={8} align="center">
            <ThemeIcon size={26} radius="md" color="red" variant="light" sx={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
              <Sparkles size={15} />
            </ThemeIcon>
            <Text fw={700} size="sm" color={isDark ? '#fff' : '#1E293B'}>
              Flow Elements
            </Text>
          </Group>

          <Group spacing={6} align="center">
            <Badge size="xs" variant="light" color="gray">
              Drag & Drop
            </Badge>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              onClick={() => setIsCollapsed(true)}
              title="Collapse Flow Elements"
            >
              <ChevronLeft size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Search Input */}
        <Box pt="xs" pb="xs">
          <TextInput
            size="xs"
            radius="md"
            icon={<Search size={14} color="#94A3B8" />}
            placeholder="Search blocks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            styles={{
              input: {
                backgroundColor: isDark ? '#141517' : '#F8FAFC',
                borderColor: isDark ? '#373A40' : '#E2E8F0',
                '&:focus': {
                  borderColor: '#DC2626',
                },
              },
            }}
          />
        </Box>

        {/* Categorized Elements List */}
        <ScrollArea sx={{ flex: 1 }} offsetScrollbars scrollbarSize={6}>
          <Stack spacing="md" pr={4}>
            {filteredCategories.map((cat, cIdx) => (
              <Box key={cIdx}>
                {/* Category Header */}
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    backgroundColor: isDark ? '#1a1b1e' : '#ffffff',
                    color: isDark ? '#94A3B8' : '#64748B',
                    borderBottom: isDark ? '1px solid #2C2E33' : '1px solid #F1F5F9',
                    marginBottom: '6px',
                  }}
                >
                  {cat.name}
                </Box>

                <Stack spacing={6}>
                  {cat.items.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <Box
                        key={idx}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('application/reactflow', JSON.stringify({ type: item.type, data: item.data }));
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onClick={() => onAddNode(item.type, item.data)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          border: isDark ? '1px solid #2C2E33' : '1px solid #E2E8F0',
                          backgroundColor: isDark ? '#25262B' : '#FFFFFF',
                          cursor: 'grab',
                          userSelect: 'none',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            borderColor: '#DC2626',
                            backgroundColor: isDark ? '#2C2E33' : '#FFF5F5',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                          },
                          '&:active': {
                            cursor: 'grabbing',
                          },
                        }}
                      >
                        <ThemeIcon
                          size={30}
                          radius="md"
                          color={item.color}
                          variant="light"
                          sx={{ flexShrink: 0 }}
                        >
                          <Icon size={16} />
                        </ThemeIcon>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text size="xs" fw={600} color={isDark ? '#F1F5F9' : '#1E293B'} truncate>
                            {item.title}
                          </Text>
                          <Text size="10px" color={isDark ? '#94A3B8' : '#64748B'} truncate>
                            {item.desc}
                          </Text>
                        </div>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        </ScrollArea>

        {/* Palette Footer Tip */}
        <Box
          pt="xs"
          mt="xs"
          sx={{
            borderTop: isDark ? '1px solid #2C2E33' : '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontSize: '10px',
            color: isDark ? '#64748B' : '#94A3B8',
          }}
        >
          <Lightbulb size={14} color="#F59E0B" />
          <Text size="10px" color="dimmed">
            Drag or click blocks to add to flow
          </Text>
        </Box>
      </Paper>
    </>
  );
};
